<template lang="pug">
b-card
  b-row(v-if="isLoading || isSubmitting")
    b-col
      b-card(class="py-5")
        loading(
          :active="true" 
          :is-full-page="false"
          color="#498ceb"
        )
  validation-observer(v-else, ref="form", tag="form")
    b-form
      b-row
        // Control
        b-col(md="12")
          b-form-group(:label="$t('label.control')", :label-for="$t('label.control')", description="")
            validation-provider(:name="$t('label.control')", rules="required", v-slot="{ errors }")
              b-form-input(
                v-model="data.name",
                trim,
                autofocus,
                :placeholder="$t('placeholder.control')"
              )
              small(class="text-danger") {{ errors[0] }}

      b-row
        // Responsable
        b-col(md="12")
          b-form-group(:label="$t('label.responsible')", :label-for="$t('label.responsible')")
            v-select(
              v-model="data.responsible_id",
              :reduce="(worker) => worker.value",
              :options="workersTotal",
              :selectable="worker => !worker.deleted",
              :placeholder="$t('placeholder.responsible')",
              label="title"
            )

      b-row
        // Behaviours
        b-col(md="12")
          b-form-group(:label="$t('label.behaviours')", :label-for="$t('label.behaviours')")
            draggable(v-model="behaviours" tag="ul" class="draggable-list")
              li(v-for="(behaviour, idx) in behaviours", :key="idx", class="draggable-item")
                feather-icon(icon="MenuIcon", class="menu-icon")
                span.mr-1 {{ `${idx + 1}.` }}
                v-select(
                  v-model="behaviours[idx]"
                  class="behaviour-select"
                  label="title"
                  :options="behaviourOptions"
                  :reduce="(option) => option.value"
                  :placeholder="$t('placeholder.behaviourSelect')"
                  :selectable="(option) => !behaviours.includes(option.value)"
                  :append-to-body="true"
                )
                small(v-show="!behaviour", class="text-danger ml-1") {{ validationMessage }}
                b-button(v-show="!!behaviour", variant="outline-secondary", class="ml-2", @click="openModal(idx, behaviourOptionsComplete.find(e => e._id === behaviour))")
                  feather-icon(icon="EditIcon", class="mr-25")
                  span {{ $t('Edit') }}
                b-button(variant="outline-success", class="ml-2", @click="openModal(idx)")
                  feather-icon(icon="PlusIcon", class="mr-25")
                  span {{ $t('message.create') }}
                b-button(variant="outline-danger", class="ml-2", @click="removeBehaviour(idx)")
                  feather-icon(icon="XIcon", class="mr-25")
                  span {{ $t('message.delete') }}

      b-row
        // Add behaviour button
        b-col(md="12")
          b-button(class="mt-1", variant="primary", @click="addBehaviour")
            feather-icon(icon="PlusIcon", class="mr-25")
            span {{ $t('message.Add_New_Behaviour') }}

      // Submit Button
      b-row
        b-col(cols="12", class="text-right")
          b-button.mr-2(
            v-if="isFromRisk",
            variant="outline-secondary",
            :disabled="isSubmitting",
            @click="$emit('cancel')"
          )
            | {{ $t('message.cancel') }}

          b-button(
            variant="success",
            :disabled="isSubmitting",
            @click="validateAndSubmit"
          )
            | {{ isFromRisk ? isFromRiskEdit ? $t('message.editControl') : $t('message.createControl') : $t('message.submit') }}

  // Modal for creating new behaviour or editing existing behaviour
  b-modal(
    id="behaviour-modal"
    v-model="isBehaviourModalVisible"
    no-close-on-backdrop
    ok-variant="success"
    :title="isEditMode ? $t('Edit this Behaviour') : $t('Add a new Behaviour')"
    :ok-title="isEditMode ? $t('Edit') : $t('message.create')"
    :cancel-title="$t('message.cancel')"
    @ok="saveNewBehaviour"
    @hidden="resetNewBehaviourData"
  )
    loading(
      v-if="isProcessingBehaviour"
      :active="true" 
      :is-full-page="false"
      color="#498ceb"
    )

    validation-observer(ref="modalValidation", tag="form")
      b-form-group(:label="$t('label.behaviour')")
        validation-provider(:name="$t('label.behaviour')", rules="required", v-slot="{ errors }")
          b-form-textarea(v-model="newBehaviour.name" :placeholder="$t('placeholder.behaviour')") 
          small(class="text-danger") {{ errors[0] }}

      b-form-group(:label="$t('label.Type')")
        validation-provider(:name="$t('label.Type')", rules="required", v-slot="{ errors }")
          v-select(v-model="newBehaviour.type", label="title", :options="types", :reduce="(type) => type.value") 
          small(class="text-danger") {{ errors[0] }}

      b-form-group(v-if="newBehaviour.type === 'bool'", :label="$t('label.expected_answer')")
        validation-provider(:name="$t('label.expected_answer')", rules="required", v-slot="{ errors }")
          v-select(v-model="newBehaviour.expectedAnswer", label="title", :options="yesNoOptions", :reduce="(option) => option.value")
          small(class="text-danger") {{ errors[0] }}

      b-form-group(:label="$t('label.requires_evidence')")
        validation-provider(:name="$t('label.requires_evidence')", rules="required", v-slot="{ errors }")
          v-select(v-model="newBehaviour.requiresEvidence", label="title", :options="yesNoOptions", :reduce="(option) => option.value")
          small(class="text-danger") {{ errors[0] }}

  audit-log-table(
    v-if="isEdit && data",
    :history="data.history || []",
    :field-translations="{ name: $t('label.control'), responsible: $t('label.responsible'), responsible_id: $t('label.responsible'), behaviours: $t('label.behaviours') }"
  )
</template>

<script>
import { ref, onMounted, computed, watch } from '@vue/composition-api/dist/vue-composition-api'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from "@validations";
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import vSelect from 'vue-select'
import { useLambda } from '@/views/habit/awsLambda'
import store from '@/store'
import draggable from 'vuedraggable'
import { types, yesNoOptions } from '@/constants'
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { oid } from '@/helpers/mongoHelpers'
import useCommon from '@/views/organization/useCommon'
import AuditLogTable from '@/components/AuditLogTable.vue'

export default {
  name: 'Form',

  components: {
    ValidationObserver,
    ValidationProvider,
    vSelect,
    draggable,
    Loading,
    AuditLogTable,
  },

  props: {
    data: { type: Object, required: true },
    isSubmitting: { type: Boolean, default: false },
    isFromRisk: { type: Boolean, default: false },
    isFromRiskEdit: { type: Boolean, default: false },
    isEdit: { type: Boolean, default: false },
  },

  setup(props, { emit }) {
    const { showErrorMessage } = useNotifications()
    const { getItems, createItem, updateItem } = useLambda()
    const { getWorkersTotalForDropDown, workersTotal } = useCommon()
    const form = ref(null)
    const userData = store.state?.userStore?.userData
    const behaviours = ref(props.data?.behaviours || [])

    // Si el control ya tiene responsible_id no hacemos nada; si no lo tiene, intentamos
    // precargarlo matcheando por nombre exacto contra el texto libre viejo (data.responsible)
    watch([() => props.data, workersTotal], () => {
      if (!props.data || props.data.responsible_id || !props.data.responsible || !workersTotal.value?.length) return

      const match = workersTotal.value.find(worker => worker.title?.trim().toLowerCase() === props.data.responsible.trim().toLowerCase())
      if (match) props.data.responsible_id = match.value
    }, { immediate: true })
    const behaviourOptions = ref([])
    const behaviourOptionsComplete = ref([])
    const validationMessage = computed(() => i18n.t('label.required'))
    const isSubmitted = ref(false)
    const isBehaviourModalVisible = ref(false);
    const newBehaviour = ref({ name: '', type: 'bool', expectedAnswer: true, requiresEvidence: false });
    const modalValidation = ref(null)
    const isProcessingBehaviour = ref(false)
    const collection = 'behaviour'
    const behaviourActiveIndex = ref(null)
    const isEditMode = ref(false)
    const isLoading = ref(true)

    const openModal = (idx, behaviourData) => {
      if (behaviourData) {
        newBehaviour.value = {
          _id: behaviourData._id,
          name: behaviourData.name,
          type: behaviourData.type,
          expectedAnswer: behaviourData.expectedAnswer,
          requiresEvidence: behaviourData.requiresEvidence
        }
        isEditMode.value = true
      } else {
        isEditMode.value = false
      }
      behaviourActiveIndex.value = idx
      isBehaviourModalVisible.value = true
    }

    const resetNewBehaviourData = () => {
      newBehaviour.value = { name: '', type: 'bool', expectedAnswer: true, requiresEvidence: false }
    }

    const saveNewBehaviour = (event) => {
      event.preventDefault()
      validateForm(modalValidation.value)
        .then(async () => {
          // Create new behaviour or edit existing behaviour
          try {
            isProcessingBehaviour.value = true

            const payload = {
              name: newBehaviour.value.name,
              type: newBehaviour.value.type,
              requiresEvidence: newBehaviour.value.requiresEvidence,
            }

            if (payload.type === 'bool') payload.expectedAnswer = newBehaviour.value.expectedAnswer
            
            if (isEditMode.value) {
              // Edit existing behaviour
              const query = { _id: oid(newBehaviour.value._id) }
              const action = { $set: payload }

              await updateItem({ collection, query, action })
              await fetchBehaviours()
            } else {
              // Create new behaviour
              payload.client_id = oid(userData.client.$oid)

              const { insertedId } = await createItem({ collection, payload })
  
              const newOption = {
                title: newBehaviour.value.name,
                value: insertedId.toString(),
              };
  
              behaviourOptions.value.push(newOption);
              behaviours.value[behaviourActiveIndex.value] = newOption.value;
            }

            // Clear modal data
            resetNewBehaviourData()
            isBehaviourModalVisible.value = false;
          } catch (error) {
            console.log(error)
            showErrorMessage(isEditMode.value ? i18n.t('message.behaviour_edit_error') : i18n.t('message.behaviour_add_error'))
          } finally {
            isProcessingBehaviour.value = false
          }
        })
        .catch(() => {
          showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
        })
    };

    const addBehaviour = () => {
      behaviours.value.push('')
    }

    const removeBehaviour = (idx) => {
      behaviours.value.splice(idx, 1)
    }

    watch(props, val => {
      if (val.data?.behaviours && !isSubmitted.value) behaviours.value = val.data.behaviours
    })

    const validateForm = (formRef) => new Promise((resolve, reject) => {
      formRef.validate().then(success => {
        if (success) resolve(true)
        else reject()
      })
    })

    const validateAndSubmit = () => {
      // Check if there are missing or repeated behaviours
      const behaviourRegistry = {}

      for (const behaviour of behaviours.value) {
        if (!behaviour)  return showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
        if (behaviourRegistry[behaviour]) return showErrorMessage(i18n.t('message.behavioursRepeated'))
        behaviourRegistry[behaviour] = 1
      }

      validateForm(form.value)
        .then(() => {
          isSubmitted.value = true

          const payload = {
            _id: props.data._id,
            name: props.data.name,
            responsible_id: props.data.responsible_id || null,
            behaviours: behaviours.value
          }

          // Control existente: mantiene sincronizado el texto libre viejo (responsible) con el
          // worker elegido, ya que puede tener datos previos que otras pantallas todavía leen.
          // Control nuevo: no hace falta guardar texto libre, responsible_id ya alcanza.
          if (props.data._id) {
            const selectedWorker = workersTotal.value.find(worker => worker.value === props.data.responsible_id)
            payload.responsible = selectedWorker?.title || props.data.responsible || ''
          }

          emit('submit', payload)
        })
        .catch(() => showErrorMessage(i18n.t('message.requiredFieldsIncomplete')))
    }

    const fetchBehaviours = async () => {
      try {
        const query = {
          client_id: oid(userData?.client?.$oid),
          deleted: { $ne: true }
        }

        const items = await getItems({ collection, query, options: { sort: { name: 1 } } })

        items?.forEach(e => {
          e._id = e._id.toString()
          e.name = e.name?.replace(/<[^>]*>/g, '') || ''
        })

        behaviourOptionsComplete.value = items || []
        behaviourOptions.value = items?.map(e => ({ title: e.name, value: e._id })) || []
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.data_fetch_error'))
      } finally {
        isLoading.value = false
      }
    }

    onMounted(() => {
      fetchBehaviours()
      getWorkersTotalForDropDown()
    })

    return {
      required,
      form,
      workersTotal,
      validateAndSubmit,
      behaviourOptions,
      behaviourOptionsComplete,
      validationMessage,
      addBehaviour,
      removeBehaviour,
      behaviours,
      isBehaviourModalVisible,
      newBehaviour,
      types,
      yesNoOptions,
      saveNewBehaviour,
      modalValidation,
      isProcessingBehaviour,
      openModal,
      resetNewBehaviourData,
      isEditMode,
      isLoading,
    }
  },
}
</script>

<style scoped>
.draggable-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.draggable-item {
  display: flex;
  align-items: center;
  padding: 10px;
  margin: 5px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.draggable-item:hover {
  background-color: #f9f9f9;
}

.menu-icon {
  margin-right: 14px;
  cursor: move;
}

.behaviour-select {
  flex-grow: 1;
}
</style>
